// Story 7.1 AC2 — Preview lateral durante edição de bundle.
// Mostra "cliente recebe: SaaS A Plano X, SaaS B Plano Y" em tempo real.

import { Package } from 'lucide-react';
import { useMemo } from 'react';

import type { ExternalPlanEntity } from '../../lib/external-plans-api';
import type { SaasProductEntity } from '../../lib/saas-products-api';

interface PreviewEntitlement {
  saas_product_id: string;
  external_plan_id: string;
  limits_override?: Record<string, unknown> | unknown;
  display_order?: number;
}

interface EntitlementsPreviewProps {
  entitlements: PreviewEntitlement[];
  saasProducts: SaasProductEntity[];
  externalPlans: ExternalPlanEntity[];
}

/**
 * Painel lateral que mostra o que o cliente vai receber ao contratar
 * o plano. Renderiza em ordem de display_order (drag&drop final).
 */
export function EntitlementsPreview({
  entitlements,
  saasProducts,
  externalPlans,
}: EntitlementsPreviewProps): JSX.Element {
  const saasById = useMemo(() => new Map(saasProducts.map((s) => [s.id, s])), [saasProducts]);
  const externalById = useMemo(() => new Map(externalPlans.map((p) => [p.id, p])), [externalPlans]);

  const ordered = useMemo(() => {
    return [...entitlements].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
  }, [entitlements]);

  const completeCount = ordered.filter((e) => e.saas_product_id && e.external_plan_id).length;

  return (
    <aside className="sticky top-4 flex flex-col gap-3 rounded-[12px] border border-line bg-panel p-4">
      <div className="flex items-center gap-2">
        <Package size={14} strokeWidth={1.8} className="text-brand" />
        <h3 className="text-[12px] font-semibold uppercase tracking-wider text-muted-3">
          Cliente vai receber acesso a
        </h3>
      </div>

      {completeCount === 0 ? (
        <p className="rounded-[8px] border border-dashed border-line bg-panel-2 p-3 text-[12px] text-muted">
          Preencha SaaS + plano externo para ver o preview.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {ordered.map((ent, idx) => {
            const saas = saasById.get(ent.saas_product_id);
            const external = externalById.get(ent.external_plan_id);
            if (!saas || !external) {
              return (
                <li
                  key={idx}
                  className="rounded-[8px] border border-dashed border-line bg-panel-2 p-2 text-[12px] text-muted"
                >
                  <span className="font-mono text-muted-3">#{idx + 1}</span> Selecione SaaS e plano…
                </li>
              );
            }
            const overrideKeys = Object.keys(
              (ent.limits_override as Record<string, unknown> | undefined) ?? {},
            );
            return (
              <li
                key={idx}
                className="flex flex-col gap-1 rounded-[8px] border border-line bg-panel-2 p-3"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[13px] font-medium text-ink">{saas.name}</span>
                  <span className="font-mono text-[10px] text-muted-3">#{idx + 1}</span>
                </div>
                <span className="text-[12px] text-muted">{external.name}</span>
                {overrideKeys.length > 0 && (
                  <span className="mt-1 inline-flex w-fit rounded-[6px] border border-warn/40 bg-warn/10 px-2 py-[1px] text-[10px] font-medium uppercase tracking-wider text-warn">
                    {overrideKeys.length} override{overrideKeys.length === 1 ? '' : 's'}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <p className="mt-1 text-[11px] text-muted-2">
        {completeCount}/{ordered.length} entitlement{ordered.length === 1 ? '' : 's'} pronto
        {completeCount === 1 ? '' : 's'}.
      </p>
    </aside>
  );
}
